import React, { useState, useEffect, useContext, useRef } from 'react';
import HorizontalTabPanel from '../HorizontalTabPanel/HorizontalTabPanel';
import Panel from '../TabPanel/Panel';
import ProcessChanges from '../ProcessChanges/ProcessChanges';
import CorporationManagement from '../CorporationManagement/CorporationManagement';
import FacilityManagement from '../FacilityManagement/FacilityManagement';
import CorporationDetailModal from '../CorporationDetailModal/CorporationDetailModal';
import FacilityDetailModal from '../FacilityDetailModal/FacilityDetailModal';
import NewOrganizationModal from '../ProcessChanges/NewOrganizationModal';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import logComponent from '../../common/logComponent';

export default function OrgManagement({ updateCurrentSubPanel, currentPanel }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const { activeUser: user, hasToken } = environmentContext;
  const { stagedOrganizations, corporations, facilities } = adminContext;
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [visibleOrganization, setVisibleOrganization] = useState(null);
  const [updatedFacility, setUpdatedFacility] = useState(null);
  const [updatedCorporation, setUpdatedCorporation] = useState(null);
  const [updatedOrganization, setUpdatedOrganization] = useState(null);// can I tell this apart from added?
  const [addedOrganization, setAddedOrganization] = useState(null);
  const [rejectedOrganization, setRejectedOrganization] = useState(null);

  if (logComponent('OrgManagement')) { console.debug('OrgManagement rendered'); }

  useEffect(() => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement onMount'); }
    if (!hasToken) return;
    loadOrganizations();
  }, [])

  useEffect(() => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadOrganizations();
  }, [environmentContext.hasToken])

  const loadOrganizations = () => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement loadOrganizations'); }
    if (!hasToken) return;

    if (adminContext.isFresh('stagedOrganizations') && 
        adminContext.isFresh('corporations') && 
        adminContext.isFresh('facilities')) return;

    setIsLoading(true);
    setIsError(false);

    convenienceApi.getAllOrganizations()
      .then(
        organizations => {
          adminContext.setAllOrganizations(organizations);
          setIsLoading(false);
          setIsError(false);
        },
        error => {
          setIsLoading(false);
          setIsError(true);
        }
      );
  }

  const setVisibleOrganizationThis = (org) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement setVisibleOrganizationThis'); }
    // Get linked entities for organization
    if (org.uiData.linkedFacilityIds) {
      org.uiData.linkedFacilities = getLinkedFacilities(org);
    } else if (org.uiData.linkedCorporationIds) {
      org.uiData.linkedCorporations = getLinkedCorporations(org);
    }

    // If org has a relatedMimiEntity, get linked entities for that as well
    if (org.uiData.relatedMimiFacility) {
      org.uiData.relatedMimiFacility.uiData.linkedCorporations = getLinkedCorporations(org.uiData.relatedMimiFacility);
    } else if (org.uiData.relatedMimiCorporation) {
      org.uiData.relatedMimiCorporation.uiData.linkedFacilities = getLinkedFacilities(org.uiData.relatedMimiCorporation);
    }

    setVisibleOrganization(org);
  }    

  const getLinkedFacilities = (corp) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement getLinkedFacilities'); }

    let linkedFacilities = [];

    corp.uiData.linkedFacilityIds.forEach(id => {
      let facility = stagedOrganizations.find(org => org.salesforceAccountId === id);

      if (!facility) {
        facility = facilities.find(facility => facility.salesforceAccountId === id);
      }

      if (facility) linkedFacilities.push(facility);
    });

    return linkedFacilities;
  }

  const getLinkedCorporations = (facility) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement getLinkedCorporations'); }
    let linkedCorporations = [];

    facility.uiData.linkedCorporationIds.forEach(id => {
      let corporation = stagedOrganizations.find(org => org.salesforceAccountId === id);

      if (!corporation) {
        corporation = corporations.find(corporation => corporation.salesforceAccountId === id);
      }

      if (corporation) linkedCorporations.push(corporation);
    });

    return linkedCorporations;    
  }

  const updateOrganizationInUI = (organization) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement updateOrganizationInUI'); }
    if (organization.corporationId) {
      updateCorporationInUI(organization);
    } else if (organization.facilityId) {
      updateFacilityInUI(organization);
    }
  }

  const updateCorporationInUI = (updatedCorporation) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement updateCorporationInUI'); }
    let newCorporations = adminContext.corporations.map(corporation => 
      (corporation.corporationId === updatedCorporation.corporationId) ? Object.assign(corporation, updatedCorporation) : corporation);

    adminContext.setCorporations(newCorporations);

    setUpdatedCorporation(() => window.scrollTo(0, 0));
  }

  const updateFacilityInUI = (updatedFacility) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement updateFacilityInUI'); }

    let newFacilities = adminContext.facilities.map(facility => 
      (facility.facilityId === updatedFacility.facilityId) ? Object.assign(facility, updatedFacility) : facility);

    adminContext.setFacilities(newFacilities);

    setUpdatedFacility(() => window.scrollTo(0, 0));
  }

  const hideVisibleOrganization = () => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement hideVisibleOrganization'); }
    setVisibleOrganization(null);
  }
  
  const removeStagedOrganization = (organizationToRemove) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement removeStagedOrganization'); }
    const filteredOrganizations = 
            adminContext.stagedOrganizations.filter(organization => organization.salesforceAccountId !== organizationToRemove.salesforceAccountId);

    adminContext.setStagedOrganizations(filteredOrganizations);
  }

  const addMimiOrganization = (newOrg) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement addMimiOrganization'); }
    if (newOrg.corporationId) {
      const orgIsNew = !adminContext.corporations.some(corporation => corporation.salesforceAccountId === newOrg.salesforceAccountId);

      const newCorporations = [newOrg, ...adminContext.corporations.filter(corporation => corporation.salesforceAccountId !== newOrg.salesforceAccountId)];
      const newStagedOrganizations = adminContext.stagedOrganizations.filter(oldOrg => oldOrg.salesforceAccountId !== newOrg.salesforceAccountId);

      adminContext.setStagedOrganizations(newStagedOrganizations);
      adminContext.setCorporations(newCorporations);      

      setAddedOrganization(orgIsNew ? newOrg : null);
      setUpdatedOrganization(orgIsNew ? null : newOrg);
      setRejectedOrganization(null);
      
    } else if (newOrg.facilityId) {
      const orgIsNew = !adminContext.facilities.some(facility => facility.salesforceAccountId === newOrg.salesforceAccountId);

      const newFacilities = [newOrg, ...adminContext.facilities.filter(facility => facility.salesforceAccountId !== newOrg.salesforceAccountId)];
      const newStagedOrganizations = adminContext.stagedOrganizations.filter(oldOrg => oldOrg.salesforceAccountId !== newOrg.salesforceAccountId);

      adminContext.setStagedOrganizations(newStagedOrganizations);
      adminContext.setFacilities(newFacilities);    

      setAddedOrganization(orgIsNew ? newOrg : null);
      setUpdatedOrganization(orgIsNew ? null : newOrg);
      setRejectedOrganization(null);
    }
  }

  const updateCurrentPanel = (subPanelTitle) => {
    if (logComponent('OrgManagement')) { console.debug('OrgManagement updateCurrentPanel'); }
   updateCurrentSubPanel('Organizations', subPanelTitle);
  }


  return (
    <div className="Organization-Management">
      <HorizontalTabPanel
        currentPanel={currentPanel}
        updateCurrentPanel={updateCurrentPanel}
        tabTitle="Organization Management">
        <Panel
          tabTitle="New & Updated"
          tabBadge={stagedOrganizations && stagedOrganizations.length > 0 ? stagedOrganizations.length : null}
          linkPath="/admin/updates"
          panelTitle="New & Updated"
          hideIf={user.groupTypeString !== 'Status Solutions' || !user.isAdmin}>
          <ProcessChanges
            isLoading={isLoading}
            isError={isError}
            reload={loadOrganizations}
            showOrganizationDetail={setVisibleOrganizationThis}
            updatedOrganization={updatedOrganization}
            addedOrganization={addedOrganization} />
        </Panel>
        <Panel
          tabTitle="Facilities"
          panelTitle="Facilities"
          linkPath="/admin/facilities"
          hideIf={user.groupTypeString !== 'Status Solutions'}>
          <FacilityManagement
            facilities={facilities}
            isLoading={isLoading}
            isError={isError}
            reload={loadOrganizations}
            showFacilityDetail={setVisibleOrganizationThis}
            updatedFacility={updatedFacility} />
        </Panel>
        <Panel
          tabTitle="Corporations"
          panelTitle="Corporations"
          linkPath="/admin/corporations"
          hideIf={user.groupTypeString !== 'Status Solutions'}>
          <CorporationManagement
            isLoading={isLoading}
            isError={isError}
            reload={loadOrganizations}
            showCorporationDetail={setVisibleOrganizationThis}
            updatedCorporation={updatedCorporation} />
        </Panel>
      </HorizontalTabPanel>
      {
        visibleOrganization &&
        !!visibleOrganization.corporationId &&
        <CorporationDetailModal
          adminUser={user}
          corporation={visibleOrganization}
          hide={hideVisibleOrganization}
          changeOrganization={setVisibleOrganizationThis}
          updateCorporation={updateOrganizationInUI}
        />
      }
      {
        visibleOrganization &&
        !!visibleOrganization.facilityId &&
        <FacilityDetailModal
          adminUser={user}
          facility={visibleOrganization}
          hide={hideVisibleOrganization}
          changeOrganization={setVisibleOrganizationThis}
          updateFacility={updateOrganizationInUI}
        />
      }
      {
        visibleOrganization &&
        !visibleOrganization.corporationId &&
        !visibleOrganization.facilityId &&
        <NewOrganizationModal
          adminUser={user}
          organization={visibleOrganization}
          hide={hideVisibleOrganization}
          addOrganization={addMimiOrganization}
          removeOrganization={removeStagedOrganization}
          changeOrganization={setVisibleOrganizationThis}
        />
      }
    </div>
  );
}